/* screens/auth/DealerInviteAccept.jsx — приглашение дилера глазами клиента (/partner/invite/:token, #509).
   Клиент сам подтверждает партнёра: только после «Подтвердить» он становится клиентом дилера и тот
   может выдавать ему тарифы. Гость — на вход/регистрацию с возвратом сюда (`?next=` в
   sessionStorage — переживает подтверждение почты только в этой же вкладке; в другой вкладке или
   на другом устройстве ссылку открывают снова, о чём говорит подсказка). Клиент другого дилера
   переходит только явной кнопкой (switch); согласие проверяет сервер под замком закрепления.
   Состояния и тексты — сервер (dealers.partner.invite_view / accept_invite). */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { ScreenHeader } from '../../components/ui/Metric.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { acceptDealerInvite, getDealerInvite } from '../../lib/api/dealers.js';
import { RR } from '../../lib/router.jsx';

const { useState, useEffect } = React;
const { useNavigate, useParams, Link } = RR;

const q = (n) => (/[«»]/.test(n) ? n : `«${n}»`);

/** Куда вести гостя: путь этой страницы как `next` для входа и регистрации. */
export function dealerInviteAuthPath(kind, token) {
  return `/${kind}?next=${encodeURIComponent(`/partner/invite/${token}`)}`;
}

function DealerInviteAccept() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const [view, setView] = useState(null);
  const [phase, setPhase] = useState(user ? 'loading' : 'guest'); // guest | loading | missing | ready | accepting | done
  const [error, setError] = useState(null);
  const [switchFrom, setSwitchFrom] = useState(null);
  const [doneName, setDoneName] = useState('');

  useEffect(() => {
    if (!user) { setPhase('guest'); return undefined; }
    setPhase('loading');
    let alive = true;
    getDealerInvite(token).then((r) => {
      if (!alive) return;
      if (!r.ok) { setPhase('missing'); return; }
      setView(r.data);
      setSwitchFrom(r.data.current_dealer_name || null);
      setPhase('ready');
    });
    return () => { alive = false; };
  }, [token, user]);

  const accept = async (sw) => {
    setPhase('accepting');
    setError(null);
    const r = await acceptDealerInvite(token, { switch: !!sw });
    if (r.ok) {
      setDoneName(r.data.dealer_name);
      setPhase('done');
      toast.show(`${q(r.data.dealer_name)} — ваш партнёр`, 'success');
      return;
    }
    const d = r.data || {};
    // прежний партнёр появился между открытием и нажатием — спрашиваем явно, без молчаливого перехода
    if (d.code === 'switch_required') setSwitchFrom(d.current_dealer_name || 'другой партнёр');
    setError(d.detail || 'Не удалось подтвердить партнёра. Попробуйте ещё раз.');
    setPhase('ready');
  };

  const home = user ? '/chat' : '/signin';
  const terminal = (icon, tone, title, sub) => (
    <div className="auth-form auth-status stack stack-16">
      <div className={`auth-status__glyph auth-status__glyph--${tone}`} aria-hidden="true"><Icon name={icon} size={30} /></div>
      <ScreenHeader align="center" title={title} subtitle={sub} />
      <Button variant="secondary" size="lg" className="full" onClick={() => navigate(home)}>{user ? 'В Ресториум' : 'Перейти ко входу'}</Button>
    </div>
  );

  if (phase === 'guest') {
    return (
      <div className="auth-form auth-invite stack stack-16">
        <div className="auth-invite__crest" aria-hidden="true"><Icon name="user" size={28} /></div>
        <ScreenHeader align="center" eyebrow="Приглашение партнёра"
          title="Подключить партнёра Ресториума"
          subtitle="Войдите или зарегистрируйтесь — после этого вы увидите, кто вас приглашает, и подтвердите." />
        <div className="stack stack-8">
          <Button variant="primary" size="lg" className="full" onClick={() => navigate(dealerInviteAuthPath('signin', token))}>Войти</Button>
          <Button variant="secondary" size="lg" className="full" onClick={() => navigate(dealerInviteAuthPath('signup', token))}>Зарегистрироваться</Button>
        </div>
        <p className="t-caption text-dim">Регистрируетесь впервые? После подтверждения почты откройте эту ссылку ещё раз, если она не открылась сама.</p>
      </div>
    );
  }
  if (phase === 'loading') {
    return (
      <div className="auth-form auth-status stack stack-12" aria-busy="true">
        <Spinner size="lg" />
        <ScreenHeader align="center" title="Загружаем приглашение…" />
      </div>
    );
  }
  if (phase === 'missing') {
    return terminal('alert-circle', 'bad', 'Ссылка недействительна', 'Проверьте ссылку или попросите у партнёра новую.');
  }
  if (phase === 'done') {
    return terminal('check-circle', 'ok', 'Партнёр подключён',
      `${q(doneName)} теперь ваш партнёр: он может выдавать и продлевать тарифы ваших заведений.`);
  }

  const v = view || {};
  const name = v.dealer_name || 'партнёр';
  if (v.status === 'accepted') return terminal('check-circle', 'ok', 'Уже подключено', `${q(name)} — ваш партнёр.`);
  if (v.status === 'used') return terminal('x-circle', 'bad', 'Ссылка уже использована', v.message);
  if (v.status === 'revoked') return terminal('x-circle', 'bad', 'Приглашение отозвано', v.message);
  if (v.status === 'expired') return terminal('clock', 'warn', 'Срок приглашения истёк', v.message);
  if (v.status === 'dealer_inactive') return terminal('clock', 'warn', 'Подключение недоступно', v.message);
  if (v.is_dealer_member) {
    return terminal('info', 'warn', 'Это ваша ссылка', 'Вы сотрудник этого партнёра — отправьте ссылку клиенту, он подтвердит её под своим аккаунтом.');
  }

  const busy = phase === 'accepting';
  return (
    <div className="auth-form auth-invite stack stack-16">
      <div className="auth-invite__crest" aria-hidden="true"><Icon name="user" size={28} /></div>
      <ScreenHeader align="center" eyebrow="Приглашение партнёра" title={<>Подключить {q(name)}</>}
        subtitle={v.already_with_dealer ? <>{q(name)} уже ваш партнёр — подтверждение просто закроет ссылку.</>
          : <>Партнёр Ресториума будет выдавать и продлевать тарифы ваших заведений.</>} />

      <div className="auth-invite__meta">
        <div className="auth-invite__meta-row"><span className="text-dim">Партнёр</span><span>{name}</span></div>
        <div className="auth-invite__meta-row"><span className="text-dim">Ваш аккаунт</span><span className="auth-invite__email">{user.email}</span></div>
      </div>

      <div className="callout" role="note">
        <Icon name="info" size={16} />
        <span>Подтверждая, вы разрешаете партнёру выдавать и отменять тарифы для заведений, которыми вы владеете, и создавать на вас новые заведения. Партнёр будет видеть ваш email, имя и дату регистрации, а по этим заведениям — названия, тарифы и сроки, включено ли автопродление картой, какая касса подключена, когда был последний обмен с ней и текст ошибки, если обмен не идёт (в нём бывает адрес сервера кассы), когда ваша команда последний раз заходила в сервис и сколько в ней человек, сколько было запросов к Аргусу, сколько заведено блюд, полуфабрикатов и позиций сырья и у скольких позиций сырья есть цена. Выручку, продажи, содержимое рецептур, аналитику и чаты партнёр не видит. Выдача тарифа партнёром выключает автопродление картой в Ресториуме — после отмены выдачи оно само не вернётся; другой тариф вместо текущего сжигает остаток оплаченного срока. Оплачивать тариф вы будете партнёру по договору с ним.</span>
      </div>

      {switchFrom && !v.already_with_dealer && (
        <div className="callout callout--warn" role="status">
          <Icon name="alert-triangle" size={16} />
          <span>Сейчас ваш партнёр — <strong>{switchFrom}</strong>. Подтвердив, вы перейдёте к {q(name)}: новые тарифы будет выдавать он, прошлые выдачи останутся за прежним партнёром.</span>
        </div>
      )}

      {error && <div className="callout callout--bad" role="alert"><Icon name="alert-circle" size={16} /><span>{error}</span></div>}

      <div className="stack stack-8">
        {switchFrom && !v.already_with_dealer ? (
          <Button variant="primary" size="lg" className="full" loading={busy} onClick={() => accept(true)}>Перейти к {q(name)}</Button>
        ) : (
          <Button variant="primary" size="lg" className="full" loading={busy} onClick={() => accept(false)}>Подтвердить партнёра</Button>
        )}
        <Link to="/chat" className="auth-form__alt t-body text-dim auth-invite__skip">Не сейчас</Link>
      </div>
    </div>
  );
}

export { DealerInviteAccept };
